import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
interface MapData {
  region: string;
  count: number;
  latitude: number;
  longitude: number;
}
interface ChoroplethMapProps {
  data: MapData[];
}
export const ChoroplethMap: React.FC<ChoroplethMapProps> = ({
  data
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  // Ethiopia's coordinates and cities
  const ethiopiaCenter = [9.145, 40.4897]; // Center of Ethiopia
  // Update the data with real Ethiopian cities
  const ethiopianData = [{
    region: 'Addis Ababa',
    count: 245,
    latitude: 9.032,
    longitude: 38.75
  }, {
    region: 'Dire Dawa',
    count: 189,
    latitude: 9.593,
    longitude: 41.866
  }, {
    region: 'Bahir Dar',
    count: 156,
    latitude: 11.585,
    longitude: 37.39
  }, {
    region: 'Hawassa',
    count: 212,
    latitude: 7.062,
    longitude: 38.477
  }, {
    region: 'Mekelle',
    count: 178,
    latitude: 13.4967,
    longitude: 39.4697
  }, {
    region: 'Gondar',
    count: 134,
    latitude: 12.603,
    longitude: 37.452
  }, {
    region: 'Adama',
    count: 267,
    latitude: 8.54,
    longitude: 39.27
  }, {
    region: 'Jimma',
    count: 145,
    latitude: 7.678,
    longitude: 36.837
  }];
  // Find the max count to normalize the colors
  const maxCount = Math.max(...ethiopianData.map(item => item.count));
  // Function to get color intensity based on count
  const getColorIntensity = (count: number) => {
    const ratio = count / maxCount;
    return `rgba(239, 68, 68, ${0.2 + ratio * 0.8})`; // Red with varying opacity
  };
  useEffect(() => {
    if (mapRef.current && !mapInstanceRef.current) {
      // Initialize the map
      const map = L.map(mapRef.current).setView(ethiopiaCenter, 6);
      // Add OpenStreetMap tiles
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
      }).addTo(map);
      // Add country border
      fetch('https://raw.githubusercontent.com/johan/world.geo.json/master/countries/ETH.geo.json').then(response => response.json()).then(data => {
        L.geoJSON(data, {
          style: {
            color: '#555',
            weight: 2,
            fillColor: '#f5f5f5',
            fillOpacity: 0.3
          }
        }).addTo(map);
      }).catch(error => {
        console.error('Failed to load Ethiopia GeoJSON:', error);
      });
      // Add markers for hotspots
      ethiopianData.forEach(city => {
        const circleRadius = Math.max(20, city.count / 5);
        const circleColor = getColorIntensity(city.count);
        // Create circle marker
        const circle = L.circleMarker([city.latitude, city.longitude], {
          radius: circleRadius / 3,
          fillColor: circleColor,
          color: '#fff',
          weight: 1,
          opacity: 1,
          fillOpacity: 0.8
        }).addTo(map);
        // Add popup with information
        circle.bindPopup(`
          <div class="font-bold">${city.region}</div>
          <div>Offenses: ${city.count}</div>
        `);
      });
      mapInstanceRef.current = map;
    }
    // Cleanup function
    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);
  return <div className="relative h-96 bg-gray-100 rounded-lg overflow-hidden" data-id="element-78">
      <div ref={mapRef} className="absolute inset-0" data-id="element-79"></div>
      {/* Legend */}
      <div className="absolute bottom-4 right-4 bg-white p-2 rounded-md shadow z-[1000]" data-id="element-80">
        <h4 className="text-sm font-semibold mb-2" data-id="element-81">Offense Count</h4>
        <div className="flex items-center space-x-2" data-id="element-82">
          <div className="w-4 h-4 rounded-full" style={{
          backgroundColor: 'rgba(239, 68, 68, 0.2)'
        }} data-id="element-83"></div>
          <span className="text-xs" data-id="element-84">Low</span>
        </div>
        <div className="flex items-center space-x-2" data-id="element-85">
          <div className="w-4 h-4 rounded-full" style={{
          backgroundColor: 'rgba(239, 68, 68, 0.5)'
        }} data-id="element-86"></div>
          <span className="text-xs" data-id="element-87">Medium</span>
        </div>
        <div className="flex items-center space-x-2" data-id="element-88">
          <div className="w-4 h-4 rounded-full" style={{
          backgroundColor: 'rgba(239, 68, 68, 0.9)'
        }} data-id="element-89"></div>
          <span className="text-xs" data-id="element-90">High</span>
        </div>
      </div>
    </div>;
};